import type { Constraints } from '@casa/layout';
import type { Rect } from '../../elements/flex_label.ts';
import { drawFlow, type Flow, type FlowState } from '../../elements/flow.ts';
import type { IconDefinition } from '../../elements/icon.ts';
import { iconElement, textElement, type LayoutElement } from '../../elements/layout_elements.ts';
import { formatNumber } from '@casa/util';
import type { TileFont } from '../value/value_tile.ts';

/**
 * The legacy `solar` tile: solar panels at the top, the grid on the left, the house on the right
 * and the battery at the bottom, joined in the middle by four animated flows. Values and colours
 * follow `SolarTile.cs`; the constraints are those of `SolarTileView.xaml`.
 */

/** MAUI's named colours, as the legacy tile used them. */
const COLORS = {
    white: '#ffffff',
    yellow: '#ffff00',
    orange: '#ffa500',
    red: '#ff0000',
    gray: '#808080',
    green: '#008000',
    darkSeaGreen: '#8fbc8f',
    greenYellow: '#adff2f',
    black: '#000000',
} as const;

export interface SolarValues {
    load: number | null;
    pv: number | null;
    batteryCharge: number | null;
    grid: number | null;
    gridVoltage: number | null;
    batterySoc: number | null;
}

export interface SolarTileContent {
    pvText: string;
    homeLoadText: string;
    homeLoadColor: string;
    gridPowerText: string;
    gridVoltageText: string;
    batteryChargeText: string;
    batterySocText: string;
    pv: Flow;
    home: Flow;
    grid: Flow;
    battery: Flow;
}

export interface SolarIcons {
    pv?: IconDefinition | undefined;
    home?: IconDefinition | undefined;
    grid?: IconDefinition | undefined;
    battery?: IconDefinition | undefined;
}

export interface SolarFlowStates {
    pv: FlowState;
    home: FlowState;
    grid: FlowState;
    battery: FlowState;
}

/** Powers in whole watts, as every value of the tile (the owner, 2026-10-22: "always be rounded to the nearest integer"). */
const watts = (value: number, locale: string | undefined) : string => `${formatNumber(value, '0', locale)} W`;

/** What the legacy tile showed for these values. Values below 50 W in or out of the grid or battery count as nothing. */
export function solarTileContent(values: SolarValues, locale?: string, textColor: string = COLORS.white) : SolarTileContent {
    const { load, pv, batteryCharge, grid } = values;
    const homeLoadColor =
        load === null ? COLORS.gray : load < 5000 ? textColor : load < 10000 ? COLORS.yellow : load < 15000 ? COLORS.orange : COLORS.red;
    const batteryActive = batteryCharge !== null && Math.abs(batteryCharge) > 50;
    const gridActive = grid !== null && Math.abs(grid) > 50;
    const towards = (value: number) : number => (value > 0 ? -1 : value < 0 ? 1 : 0);

    return {
        pvText: pv === null ? '-' : watts(pv, locale),
        homeLoadText: load === null ? '-' : watts(load, locale),
        homeLoadColor,
        gridPowerText: gridActive ? watts(Math.abs(grid), locale) : '-',
        gridVoltageText: values.gridVoltage === null ? '?' : `${formatNumber(values.gridVoltage, '0', locale)} V`,
        batteryChargeText: batteryActive ? watts(Math.abs(batteryCharge), locale) : '-',
        batterySocText: values.batterySoc === null ? '' : `${formatNumber(values.batterySoc, '0', locale)}%`,
        pv: { speed: pv === null ? 0 : Math.min(10, Math.max(0, pv / 1000)), direction: -1, orientation: -1, color: COLORS.darkSeaGreen },
        home: { speed: load === null ? 0 : Math.min(10, Math.max(0, load / 1000)), direction: 1, orientation: 1, color: homeLoadColor },
        grid: {
            speed: gridActive ? Math.abs(grid) / 1000 : 0,
            direction: gridActive ? towards(grid) : 0,
            orientation: -1,
            color: gridActive && grid < 0 ? COLORS.green : textColor,
        },
        battery: {
            speed: batteryActive ? Math.abs(batteryCharge) / 1000 : 0,
            direction: batteryActive ? towards(batteryCharge) : 0,
            orientation: 1,
            color: batteryActive && batteryCharge < 0 ? COLORS.green : COLORS.orange,
        },
    };
}

function flowElement(constraints: Constraints, flow: Flow, state: FlowState, now: number) : LayoutElement {
    return { constraints, draw: (ctx, box) => drawFlow(ctx, box, flow, state, now) };
}

/** The MAUI `Ellipse` in the middle: black, with a 4 px white stroke inside its bounds. */
function hubElement(constraints: Constraints, color: string) : LayoutElement {
    const stroke = 4;

    return {
        constraints,
        draw: (ctx, box: Rect) => {
            const radius = Math.min(box.width, box.height) / 2 - stroke / 2;
            if (radius <= 0) {
                return;
            }

            ctx.beginPath();
            ctx.arc(box.left + box.width / 2, box.top + box.height / 2, radius, 0, Math.PI * 2);
            ctx.fillStyle = COLORS.black;
            ctx.fill();
            ctx.lineWidth = stroke;
            ctx.strokeStyle = color;
            ctx.stroke();
        },
    };
}

/** The legacy icon labels have `Fill`: sized to their ink. */
const ICON = { fill: true };
const SMALL_TEXT = 'min(parent.width,parent.height)*0.045';
const FLOW_THICKNESS = 'min(parent.width,parent.height)*0.03';

/** The tile's elements, in the legacy drawing order; the flows are painted at time `now`. */
export function solarTileElements(
    content: SolarTileContent,
    icons: SolarIcons,
    states: SolarFlowStates,
    now: number,
    font: TileFont,
    textColor: string = COLORS.white,
) : LayoutElement[] {
    const text = (align: 'left' | 'center' | 'right', valign: 'top' | 'center' | 'bottom') => ({
        family: font.family,
        metrics: font.metrics,
        fill: true,
        align,
        valign,
    });

    return [
        iconElement({ ref: 'PvIcon', top: 'parent.top*0.98', centerX: 'parent.center.x', h: 'parent.width*0.15', w: 'parent.width*0.15' }, icons.pv, textColor, ICON),
        iconElement({ ref: 'HomeIcon', right: 'parent.right*0.98', centerY: 'parent.center.Y', h: 'parent.width*0.15', w: 'parent.width*0.15' }, icons.home, textColor, ICON),
        iconElement({ ref: 'GridIcon', left: 'parent.width*0.02', centerY: 'parent.center.Y*0.97', h: 'parent.width*0.15', w: 'parent.width*0.15' }, icons.grid, textColor, ICON),
        iconElement(
            { ref: 'BatteryIcon', bottom: 'parent.bottom*0.98', centerX: 'parent.center.X', h: 'parent.width*0.2', w: 'parent.width*0.2' },
            icons.battery,
            textColor,
            ICON,
        ),
        flowElement(
            { ref: 'GridArrow', right: 'parent.center.X-5', centerY: 'parent.center.Y', left: 'GridIcon.center.X', h: FLOW_THICKNESS },
            content.grid,
            states.grid,
            now,
        ),
        flowElement(
            { ref: 'BatteryArrow', top: 'parent.center.Y+5', centerX: 'parent.center.X', bottom: 'BatteryIcon.top', w: FLOW_THICKNESS },
            content.battery,
            states.battery,
            now,
        ),
        flowElement(
            { ref: 'HomeArrow', left: 'parent.center.X+5', centerY: 'parent.center.Y', right: 'HomeIcon.left+5', h: FLOW_THICKNESS },
            content.home,
            states.home,
            now,
        ),
        flowElement(
            { ref: 'PvArrow', bottom: 'parent.center.Y-5', centerX: 'parent.center.X', top: 'PvIcon.bottom', w: FLOW_THICKNESS },
            content.pv,
            states.pv,
            now,
        ),
        textElement(
            { top: 'HomeArrow.bottom+5', right: 'HomeArrow.right-10', h: SMALL_TEXT, w: 'HomeArrow.width*0.75' },
            content.homeLoadText,
            content.homeLoadColor,
            text('right', 'top'),
        ),
        textElement(
            { top: 'GridArrow.bottom+5', left: 'GridArrow.left+10', h: SMALL_TEXT, w: 'GridArrow.width*0.75' },
            content.gridPowerText,
            textColor,
            text('left', 'bottom'),
        ),
        textElement(
            { bottom: 'GridIcon.top-2', centerX: 'GridIcon.center.X', h: 'min(parent.width,parent.height)*0.05', w: 'GridIcon.width*0.8' },
            content.gridVoltageText,
            COLORS.greenYellow,
            text('center', 'center'),
        ),
        textElement(
            { top: 'PvArrow.top+10', left: 'PvArrow.right+5', h: SMALL_TEXT, w: 'HomeArrow.width/2' },
            content.pvText,
            textColor,
            text('left', 'top'),
        ),
        textElement(
            { bottom: 'BatteryArrow.bottom-10', right: 'BatteryArrow.left-5', h: SMALL_TEXT, w: 'HomeArrow.width/2' },
            content.batteryChargeText,
            textColor,
            text('right', 'bottom'),
        ),
        textElement(
            { center: 'BatteryIcon.center', h: 'BatteryIcon.height*0.5', w: 'BatteryIcon.width*0.5' },
            content.batterySocText,
            textColor,
            text('center', 'center'),
        ),
        hubElement({ centerX: 'parent.center.X', centerY: 'parent.center.Y', w: 'parent.width*0.07', h: 'parent.width*0.07' }, textColor),
    ];
}
